import React, { useEffect, useMemo, useState } from 'react';
import { useDrawers } from 'hooks/useDrawers';
import { message } from 'antd';
import {
  DELETE_CONFIRM,
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import {
  categoryMap,
  pageTypeMap,
  typeMap,
} from 'utils/handbooks/mainEventsHandbooks';
import { useFetch } from 'hooks/useFetch';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { MainEventsService } from 'services/MainEventsService';
import { checkValue, formatDate } from 'utils';
import { MainEventsTable } from './MainEventsTable';
import { MainEventsForm } from './MainEventsForm';

function MainEvents() {
  useDocumentTitle('События');
  const [mainEvents, setMainEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNames] = useFetch('org/v1/get-service-page-types/');
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();
  const { setCount } = useHeaderChanger('События');

  useEffect(() => {
    fetchMainEvents();
  }, [page, perPage]);

  const pageNamesMap = useMemo(() => {
    const pageNamesMap = {};

    pageNames?.forEach((item) => {
      pageNamesMap[item.key] = item.value;
    });

    return pageNamesMap;
  }, [pageNames]);

  const eventsRecordFields = useMemo(() => {
    return [
      { label: 'Заголовок', key: 'title' },
      { label: 'Заголовок (каз.)', key: 'title_kz' },
      { label: 'Описание', key: 'description' },
      { label: 'Описание (каз.)', key: 'description_kz' },
      { label: 'Текст', key: 'text' },
      { label: 'Текст (каз.)', key: 'text_kz' },
      { label: 'Текст кнопки', key: 'button' },
      { label: 'Текст кнопки (каз.)', key: 'button_kz' },
      {
        label: 'Категория',
        key: 'category',
        computedValue: categoryMap[activeRecord?.category],
      },
      {
        label: 'Тип',
        key: 'type',
        computedValue: typeMap[activeRecord?.type],
      },
      {
        label: 'Пакеты',
        key: 'packages',
        computedValue: checkValue(
          activeRecord?.packages?.map((item) => item.title).join(', ')
        ),
      },
      {
        label: 'Page Type',
        key: 'page_type',
        computedValue: pageTypeMap[activeRecord?.page_type],
      },
      {
        label: 'Page Name/URL',
        computedValue: pageNamesMap[activeRecord?.page_url],
      },
      { label: 'Фоновый цвет', key: 'background_color' },
      { label: 'Фоновая картинка', key: 'background_image' },
      {
        label: 'Статус',
        key: 'is_published',
        computedValue: activeRecord?.is_published
          ? 'Опубликован'
          : 'Не опубликован',
      },
      {
        label: 'Зарегистрирован',
        key: 'authorized',
        computedValue: activeRecord?.authorized ? 'Да' : 'Нет',
      },
      { label: 'Порядок', key: 'position' },
      {
        label: 'Дата создания',
        key: 'created_at',
        computedValue: formatDate(activeRecord?.created_at),
      },
    ];
  }, [activeRecord]);

  const fetchMainEvents = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await MainEventsService.getMainEvents(
        page,
        perPage
      );

      setMainEvents(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await MainEventsService.deleteMainEvent(
        activeRecord?.id
      );

      if (isDeleted) {
        closeDrawer();
        fetchMainEvents();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="mainEvent" />

      <MainEventsTable
        items={mainEvents}
        loading={loading}
        current={page}
        pageSize={perPage}
        total={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.title}
        pageId="mainEvent"
        recordFields={eventsRecordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText={DELETE_CONFIRM}
      />

      <MainEventsForm
        isActive={formIsActive}
        initialValues={activeRecord}
        pageNames={pageNames}
        onSuccess={fetchMainEvents}
        onClose={closeForm}
      />
    </>
  );
}

export { MainEvents };
